/** @jsx jsx */
import { forwardRef, useCallback, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { DayModifiers } from 'react-day-picker';
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import formatISO from 'date-fns/formatISO';
import { PopoverDialog, usePopover } from '@balance-web/popover';
import type { InputSizeType } from '@balance-web/text-input';
import { AdornmentWrapper } from '@balance-web/text-input';
import { Adornment } from '@balance-web/text-input';
import { inputBoxSizes, useInputStyles } from '@balance-web/text-input';
import { TextInput } from '@balance-web/text-input';
import { Box } from '@balance-web/box';
import { CalendarIcon } from '@balance-web/icon';
import { useTheme } from '@balance-web/theme';
import { useFieldContext } from '@balance-web/field';
import { useForkedRef } from '@balance-web/utils';
import { Text } from '@balance-web/text';
import { Flex } from '@balance-web/flex';
import { Divider } from '@balance-web/divider';
import { makeIdFactory } from '@balance-web/utils';

import { ClearButton } from './styled';
import { Calendar } from './Calendar';
import type { CalendarProps } from './Calendar';
import type { ISODate } from './types';
import { DATE_FORMATS, dateToString, parseDateWithFormats } from './utils';
import { applyKeyboardShortcuts } from './applyKeyboardShortcuts';
import { KeyboardShotcutsLegend } from './KeyboardShotcutsLegend';

export type DatePickerProps = WithDataAttributeProp<{
  /** When true, the trigger will be disabled and the clear button is removed. */
  disabled?: boolean;
  /** When true, the value will be disabled as text */
  readOnly?: boolean;
  /** Indicate that the value does not conform to the format expected by the application. */
  invalid?: boolean;
  /** Called when the value changes. */
  onChange: (value: ISODate | undefined) => void;
  /** Called when the value is cleared. */
  onClear: () => void;
  /** The value of the calendar, displayed in the trigger. */
  value: ISODate | undefined;
  size?: InputSizeType;
  keyboardShortcutLegendPosition?: 'left' | 'right';
  id?: string;
}> &
  Pick<
    CalendarProps,
    'disabledDays' | 'fromMonth' | 'initialMonth' | 'modifiers' | 'toMonth'
  >;

const DatePickerIdFactory = makeIdFactory('date-picker', [
  'input',
  'clearButton',
  'popover',
  'shortcuts',
  'calendar',
]);

/**
 * We want all the changes in the textInput and calendar to be temporary.
 * The changes are committed to the consumer onChange only when user clicks a calendar date, hits Enter or Tab in the text input.
 * All other interactions (escape, blur etc) are considered a bailout and will cause the text input and calendar to revert to the consumer value.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      onChange,
      onClear,
      value,
      // calendar props
      disabledDays,
      fromMonth,
      initialMonth,
      modifiers,
      toMonth,
      size = 'medium',
      readOnly,
      keyboardShortcutLegendPosition = 'left',
      data,
      id,
      ...buttonProps
    },
    ref
  ) => {
    // @ts-ignore Santise invalid dom attributes
    if ('blur' in buttonProps && typeof buttonProps.blur === 'function') {
      delete buttonProps.blur;
    }

    const theme = useTheme();
    const fieldContext = useFieldContext();
    const fieldId = id || fieldContext?.id;
    const textInputRef = useRef<HTMLInputElement>(null);

    const testIds = DatePickerIdFactory(getTestIdFromDataAttributes(data));

    const textInputStyles = useInputStyles({ size, shape: 'square' });

    // NOTE: calendarDate doesn't always have to be the same as the incoming value.
    const [calendarDate, setCalendarDate] = useState<Date>(
      value ? new Date(value) : new Date()
    );
    const [textValue, setTextValue] = useState<string>(
      value ? dateToString((value as unknown) as Date) : ''
    );

    const adornmentPaddingRight =
      size === 'medium' ? theme.spacing.small : theme.spacing.xsmall;

    const { isOpen, setOpen, dialog, trigger } = usePopover({
      placement:
        keyboardShortcutLegendPosition === 'left'
          ? 'bottom-end'
          : 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    });

    // Text and calendar edits are temporary until committed (see the doc
    // comment above), so both revert to the consumer value whenever it changes
    // or the popover opens/closes — open/close is the bailout boundary.
    // Resetting during render instead of in an effect avoids painting the
    // stale state first.
    const [prevSync, setPrevSync] = useState({ isOpen, value });
    if (prevSync.value !== value || prevSync.isOpen !== isOpen) {
      setPrevSync({ isOpen, value });
      setTextValue(value ? dateToString((value as unknown) as Date) : '');
      setCalendarDate(value ? new Date(value as string) : new Date());
    }

    const openPopover = () => {
      // Select the input text on open so typing replaces the previous date.
      // Deferred so the browser first finishes the interaction that opened the
      // popover (caret placement on click, character insertion on keydown),
      // matching the previous select-after-render timing.
      if (!isOpen) {
        setTimeout(() => {
          textInputRef.current?.select();
        }, 0);
      }
      setOpen(true);
    };

    const composedTextInputRef = useForkedRef(textInputRef, trigger.ref, ref);

    const calendarProps = {
      disabledDays,
      fromMonth,
      initialMonth,
      modifiers,
      toMonth,
    };

    const onDateChange = useCallback(
      (date: Date) => {
        const isoDate = formatISO(date, { representation: 'date' }) as ISODate;

        // Only emit change if new value is different from the value passed in
        if (isoDate !== value) {
          onChange(isoDate);
        }
      },
      [onChange, value]
    );

    const handleDayClick = useCallback(
      (day: Date, { disabled }: DayModifiers) => {
        if (disabled) {
          return;
        }

        onDateChange(day);

        // wait a moment so the user has time to see the day become selected
        setTimeout(() => {
          setOpen(false);
        }, 300);
      },
      [onDateChange, setOpen]
    );

    const internalOnClear = () => {
      setTextValue('');
      onClear();
      openPopover();
    };

    const commitTextValueIfValid = () => {
      const parsed = parseDateWithFormats(textValue, DATE_FORMATS);

      if (parsed) {
        onDateChange(parsed);
        setTextValue(dateToString(parsed));
        setCalendarDate(parsed);
      } else {
        onChange(undefined);
      }
    };

    const onTextValueChange = (newValue: string) => {
      setTextValue(newValue);

      if (!newValue.length) {
        return;
      }

      const parsed = parseDateWithFormats(newValue, DATE_FORMATS);
      if (parsed) {
        setCalendarDate(parsed);
        return;
      }

      const hasSeparator = newValue.includes('/') || newValue.includes('-');
      if (!hasSeparator) {
        return;
      }

      const [currentDay, currentMonth, currentYear] = dateToString(
        calendarDate
      ).split('/');
      const separator = newValue.includes('/') ? '/' : '-';
      const parts = newValue.split(separator);

      const day = parts[0]?.padStart(2, '0') || currentDay;
      const month = parts[1]?.padStart(2, '0') || currentMonth;
      const year = parts[2] || currentYear;

      const partialDateStr = `${day}/${month}/${
        year.length === 2 ? year : year.slice(0, 4)
      }`;
      const partialDate = parseDateWithFormats(partialDateStr, DATE_FORMATS);

      if (partialDate) {
        setCalendarDate(partialDate);
      }
    };

    if (readOnly) {
      return (
        <Text
          data-balance-component="date-picker-readonly"
          size={size}
          css={{ lineHeight: `${inputBoxSizes[size]}px` }}
        >
          {textValue}
        </Text>
      );
    }

    return (
      <AdornmentWrapper
        data-balance-component="date-picker"
        data-testid={testIds.base(fieldId)}
        shape="round"
        size={size}
      >
        <Box position="relative" width="100%">
          <TextInput
            ref={composedTextInputRef}
            autoComplete="off"
            {...trigger.props}
            hideInvalidAdornment
            css={{ ...textInputStyles }}
            disabled={buttonProps.disabled}
            placeholder="dd/mm/yyyy"
            // Don't do anything onBlur because it's difficult to understand the difference between blur to enter and blur to escape.
            size={size}
            value={textValue}
            onBlur={undefined}
            onChange={(e) => {
              onTextValueChange(e.target.value);
            }}
            onClick={() => {
              openPopover();
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                setOpen(false);
                textInputRef.current?.blur(); // Don't tab to next input
                return;
              }

              if (e.key === 'Enter' || e.key === 'Tab') {
                commitTextValueIfValid();
                setOpen(false);
                return;
              }

              if (!isOpen) {
                openPopover();
              }

              applyKeyboardShortcuts({
                e,
                onDateChange: (date) => {
                  setCalendarDate(date);
                  setTextValue(dateToString(date));
                },
                baseDate: calendarDate,
              });
            }}
          />

          {(value?.length || textValue.length) && !buttonProps.disabled ? (
            <ClearButton
              css={{ position: 'absolute', right: 40, zIndex: 3 }}
              data-testid={testIds.clearButton(fieldId)}
              size={size}
              onClick={internalOnClear}
            />
          ) : null}

          <Adornment
            align="right"
            css={{
              paddingRight: adornmentPaddingRight,
              pointerEvents: 'none',
              position: 'absolute',
              zIndex: 3,
            }}
          >
            <CalendarIcon
              color={
                buttonProps.disabled
                  ? 'inputDisabled'
                  : fieldContext.invalid || buttonProps.invalid
                  ? 'critical'
                  : 'textTertiary'
              }
              size={size === 'large' ? 'medium' : size}
            />
          </Adornment>

          <PopoverDialog
            ref={dialog.ref}
            data-testid={testIds.popover(fieldId)}
            isVisible={isOpen}
            {...dialog.props}
          >
            <Flex>
              {keyboardShortcutLegendPosition === 'left' ? (
                <KeyboardShotcutsLegend
                  data={{ testid: testIds.shortcuts() }}
                />
              ) : null}
              <Calendar
                data={{ testid: testIds.calendar(fieldId) }}
                month={calendarDate}
                selectedDays={calendarDate}
                onDayClick={handleDayClick}
                {...calendarProps}
              />
              {keyboardShortcutLegendPosition === 'right' ? (
                <Divider vertical />
              ) : null}
              {keyboardShortcutLegendPosition === 'right' ? (
                <KeyboardShotcutsLegend
                  data={{ testid: testIds.shortcuts(fieldId) }}
                />
              ) : null}
            </Flex>
          </PopoverDialog>
        </Box>
      </AdornmentWrapper>
    );
  }
);
